import { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
  Input,
  Button,
  FormField,
  Select,
  SpaceBetween,
  RadioGroup,
  Form,
  Container,
  ColumnLayout,
  Checkbox,
  Box,
  TimeInput,
  Grid,
} from "@cloudscape-design/components-themed";
import { useAppDispatch, useAppSelector } from "../../app/hook";
import {
  useGetDivisionQuery,
  useGetComprehensiveCompaniesQuery,
  useGetAuthQuery,
} from "../../features/api";
import { getMessage } from "../../features/error/messages";
import {
  INITIAL_SEARCH_CRITERIA,
  setSearchCriteria,
} from "../../features/slices/application-status-list";
import { formatDate, fixedFormatDateTime } from "../../utils/format";
import {
  getDivisionItems,
  COMMON_LABELS,
  addBlank,
  getDivisionLabel,
  DivisionDict,
} from "../default-constants";
import {
  LABELS,
  PARTICULAR_RATE_FLAG_LIST,
  PLACE_APPLICATION,
  MAX_SIZE_MAIL,
  MAX_SIZE_MERCHANTS,
  getAppDetailStatusList,
  DOCOMO_APPLICANT_USER_TYPE_LIST,
  MAX_SIZE_CPID,
  getApplicationFormTypeList,
} from "./constants";
import Footer from "./Footer";
import { useHasAuthorityHook } from "../../app/useHasAuthorityHook";
import { PERMISSIONS } from "../../configs/constants";
import { getComprehensiveMerchantCodeItems } from "../../utils/common";
import { isHalfNumberValid, isLengthValid } from "../../utils/inputValidation";
import { concat, isEmpty, without } from "lodash";
import { useCodelistHook } from "../../app/useCodelistHook";
import { ClickDetail } from "@cloudscape-design/components-themed/internal/events";
import { DatePicker } from "../UI/date-picker/DatePicker";
import PeriodDatePicker from "../UI/date-picker/PeriodDatePicker";

interface SearchBarProps {
  isDocomoUser: boolean;
  isMerchantGenUser: boolean;
  isApply: boolean | undefined;
  setSkip: Dispatch<SetStateAction<boolean>>;
}

/**
 * 申請状況照会 検索エリア
 */
const SearchBar = (props: SearchBarProps) => {
  const { setSkip } = props;
  // ドコモユーザかどうか
  const isDocomoUser = props.isDocomoUser;
  // 直接加盟店ユーザかどうか
  const isMerchantGenUser = props.isMerchantGenUser;
  // ドコモ管理者と業務担当かどうか
  const isApply = props.isApply !== undefined && props.isApply ? true : false;

  // ユーザ種別
  const hasAuthority = useHasAuthorityHook(PERMISSIONS.MASTER_EDIT);

  const dispatch = useAppDispatch();
  const { searchCriteria } = useAppSelector(
    (state) => state.applicationStatusList
  );
  
  const {
    companyType,
    applicationType,
    applicationStatus,
    applicationDetailStatus,
    applicationFormType
  } = useCodelistHook();
  const [searchInput, setSearchInput] = useState(INITIAL_SEARCH_CRITERIA);
  const [serviceNameList, setServiceNameList] = useState<DivisionDict[]>([]);
  const [companyNameErrorMsg, setCompanyNameErrorMsg] = useState("");
  const [merchantsNameErrorMsg, setMerchantsNameErrorMsg] = useState("");
  const [applicantEmailErrorMsg, setApplicantEmailErrorMsg] = useState("");
  const [cpidErrorMsg, setCpidErrorMsg] = useState("");
  const fixedDateStart = searchInput.fixedTimeStart.split(" ")[0];
  const fixedTimeStart = searchInput.fixedTimeStart.split(" ")[1];
  const fixedDateEnd = searchInput.fixedTimeEnd.split(" ")[0];
  const fixedTimeEnd = searchInput.fixedTimeEnd.split(" ")[1];

  // 共通データを取得
  // 区分取得API呼び出し(親区分コード：3:契約サービス区分コード)
  const { data: contractServiceKbnItems } = useGetDivisionQuery("3");

  // docomoユーザの場合は事業者コード取得を空を設定する。それ以外の場合は自分の事業者コードを設定する
  const { companyId } = useGetAuthQuery(undefined, {
    selectFromResult: ({ data }) => ({
      companyType: data?.companyType,
      companyId: data?.companyCode,
    }),
  });

  // 包括加盟店データを取得
  const [comprehensiveMerchantListItems, setComprehensiveMerchantListItems] =
    useState<DivisionDict[]>([]);
  // 包括事業者一覧取得API
  const { data: comprehensiveMerchantListData } =
    useGetComprehensiveCompaniesQuery({
      companyCode: companyId!,
      invisibleComprehensiveCompanyNoList: [],
    });
  const appDetailStatusList = applicationDetailStatus.filter((item) =>
    getAppDetailStatusList(isDocomoUser, hasAuthority).includes(item.value)
  );
  const applicantUserTypeList = companyType.filter((item) =>
    DOCOMO_APPLICANT_USER_TYPE_LIST.includes(item.value)
  );

　const applicationFormTypeList = getApplicationFormTypeList(applicationFormType);

  useEffect(() => {
    setSearchInput(searchCriteria);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchCriteria]);

  useEffect(() => {
    setComprehensiveMerchantListItems(
      addBlank(getComprehensiveMerchantCodeItems(comprehensiveMerchantListData))
    );
  }, [comprehensiveMerchantListData]);

  useEffect(() => {
    setServiceNameList(addBlank(getDivisionItems(contractServiceKbnItems)));
  }, [contractServiceKbnItems]);

  //入力チェックエラーメッセージ
  const [applicationNoErrorMsg, setApplicationNoErrorMsg] = useState("");

  /**
   * クリアボタンを押したときの処理.
   */
  const onClear = () => {
    setSkip(true);
    setSearchInput(INITIAL_SEARCH_CRITERIA);
    setCompanyNameErrorMsg("");
    setMerchantsNameErrorMsg("");
    setApplicantEmailErrorMsg("");
    setApplicationNoErrorMsg("");
  };

  /**
   * 検索ボタンを押したときの処理.
   * @param {CustomEvent<ClickDetail>} e
   */
  const handleOnSubmit = (e: CustomEvent<ClickDetail>) => {
    e.preventDefault();

    if (
      checkApplicationNo(searchInput.applicationNo) &&
      checkCompanyName(searchInput.companyName) &&
      checkMerchantsName(searchInput.merchantName) &&
      checkApplicationCpid(searchInput.cpid)
    ) {
      setSkip(false);
      dispatch(
        setSearchCriteria({
          ...searchInput,
          applicationFormType: searchInput.applicationFormType,
          timestamp: new Date().getTime(),
        })
      );
    }
  };

  /**
   * 申請種別の入力ハンドラ
   * @param {string} value 申請種別のvalue
   */
  const updateDifferencialOnChange = (value: string) => {
    const applicationTypes = searchInput.applicationTypes;
    setSearchInput({
      ...searchInput,
      applicationTypes: applicationTypes.includes(value)
        ? without(applicationTypes, value)
        : concat(applicationTypes, value),
    });
  };

  /**
   * 申請状況の入力ハンドラ
   * @param {string} value 申請状況のvalue
   */
  const applicationStatusOnChange = (value: string) => {
    const applicationStatuses = searchInput.applicationStatuses;
    setSearchInput({
      ...searchInput,
      applicationStatuses: applicationStatuses.includes(value)
        ? without(applicationStatuses, value)
        : concat(applicationStatuses, value),
    });
  };

  /**
   * 申請状況詳細の入力ハンドラ
   * @param {string} value 申請状況詳細のvalue
   */
  const applicationDetailStatusOnChange = (value: string) => {
    const applicationDetailStatuses = searchInput.applicationDetailStatuses;
    setSearchInput({
      ...searchInput,
      applicationDetailStatuses: applicationDetailStatuses.includes(value)
        ? without(applicationDetailStatuses, value)
        : concat(applicationDetailStatuses, value),
    });
  };

  /**
   * 申請者種別の入力ハンドラ
   * @param {string} value 申請者種別のvalue
   */
  const applicantUserTypeOnChange = (value: string) => {
    const applicantUserType = searchInput.applicantUserType;
    setSearchInput({
      ...searchInput,
      applicantUserType: applicantUserType.includes(value)
        ? without(applicantUserType, value)
        : concat(applicantUserType, value),
    });
  };

    /**
   * 申請書種別の入力ハンドラ
   * @param {string} value 申請書種別のvalue
   * 
   */
    const applicationFormTypeOnChange = (value: string) => {
      const applicantFormType = searchInput.applicationFormType;

      const exists = applicantFormType.some(it => it === value);
      const updatedFormType = exists ? applicantFormType.filter((it) => it !== value) : 
          [...applicantFormType, value];

      setSearchInput({
        ...searchInput,
        applicationFormType: updatedFormType,
      });
    };

  /**
   * 申請番号のチェック
   * @param {string} applicationNo
   * @return {boolean}
   */
  const checkApplicationNo = (applicationNo: string) => {
    if (!isHalfNumberValid(applicationNo)) {
      setApplicationNoErrorMsg(getMessage("KGRERR006", "申請番号").label);
      return false;
    }
    setApplicationNoErrorMsg("");
    return true;
  };

  /**
   * 事業者名バリデーションチェック
   * @param companyName
   * @returns
   */
  const checkCompanyName = (companyName: string): boolean => {
    if (!isLengthValid(companyName, 30)) {
      setCompanyNameErrorMsg(getMessage("KGRERR058", 30).label);
      return false;
    } else {
      setCompanyNameErrorMsg("");
      return true;
    }
  };

  /**
   * 加盟店名バリデーションチェック
   * @param merchantsName
   * @returns
   */
  const checkMerchantsName = (merchantsName: string): boolean => {
    if (!isLengthValid(merchantsName, MAX_SIZE_MERCHANTS)) {
      setMerchantsNameErrorMsg(
        getMessage("KGRERR058", MAX_SIZE_MERCHANTS).label
      );
      return false;
    } else {
      setMerchantsNameErrorMsg("");
      return true;
    }
  };

  /**
   * 申請者メールチェック
   * @param applicantEmail
   * @returns
   */
  const checkApplicationEmail = (applicantEmail: string) => {
    if (!isLengthValid(applicantEmail, MAX_SIZE_MAIL)) {
      setApplicantEmailErrorMsg(getMessage("KGRERR058", MAX_SIZE_MAIL).label);
      return false;
    } else {
      setApplicantEmailErrorMsg("");
      return true;
    }
  };

    /**
   * サイトIDチェック
   * @param cpid
   * @returns
   */
    const checkApplicationCpid = (cpid: string) => {
      if (!isLengthValid(cpid, MAX_SIZE_CPID)) {
        setCpidErrorMsg(getMessage("KGRERR058", MAX_SIZE_CPID).label);
        return false;
      } else if (!isHalfNumberValid(cpid)) {
        setCpidErrorMsg(getMessage("KGRERR006", "サイトID").label);
        return false;
      } else {
        setCpidErrorMsg("");
        return true;
      }
    };

  return (
    <Form
      actions={
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="normal" onClick={handleOnSubmit}>
            {COMMON_LABELS.BTNS.SEARCH}
          </Button>
          <SpaceBetween direction="horizontal" size="m">
            {<Footer />}
          </SpaceBetween>
        </SpaceBetween>
      }
    >
      <Container
        header={
          <ColumnLayout>
            <Box float="right">
              <Button variant="normal" onClick={onClear}>
                {COMMON_LABELS.BTNS.CLEAR}
              </Button>
            </Box>
          </ColumnLayout>
        }
      >
        <SpaceBetween direction="vertical" size="xs">
          <SpaceBetween direction="vertical" size="l">
            <ColumnLayout columns={4}>
              <FormField
                label={LABELS.APPLICATION_NO}
                errorText={applicationNoErrorMsg}
                constraintText="半角数字"
              >
                <Input
                  name="applicationNo"
                  value={searchInput.applicationNo}
                  onChange={({ detail: { value } }) => {
                    setSearchInput({ ...searchInput, applicationNo: value });
                    checkApplicationNo(value);
                  }}
                />
              </FormField>
              <FormField label={LABELS.SERVICE_NAME}>
                <Select
                  selectedOption={{
                    value: searchInput.serviceCode,
                    label: getDivisionLabel(
                      serviceNameList,
                      searchInput.serviceCode
                    ),
                  }}
                  options={serviceNameList}
                  onChange={({ detail: { selectedOption } }) => {
                    setSearchInput({
                      ...searchInput,
                      serviceCode: selectedOption.value!,
                    });
                  }}
                />
              </FormField>
              {!isMerchantGenUser && (
                <Grid gridDefinition={[{ colspan: 3 }, { colspan: 9 }]}>
                  <FormField label={LABELS.CONTRACT_STATUS}>
                    <RadioGroup
                      items={[
                        {
                          value: "",
                          label: LABELS.ALL,
                        },
                        {
                          value: LABELS.CONTRACT_DIRECTLY,
                          label: LABELS.CONTRACT_DIRECTLY,
                        },
                        {
                          value: LABELS.CONTRACT_COMPREHENSIVE,
                          label: LABELS.CONTRACT_COMPREHENSIVE,
                        },
                      ]}
                      onChange={({ detail: { value } }) => {
                        setSearchInput({
                          ...searchInput,
                          contractType: value,
                          contractRoute: "",
                        });
                      }}
                      value={searchInput.contractType}
                    />
                  </FormField>
                  <FormField label={LABELS.CONTRACT_ROUTE}>
                    <Select
                      disabled={
                        searchInput.contractType !==
                        LABELS.CONTRACT_COMPREHENSIVE
                      }
                      selectedOption={{
                        value: searchInput.contractRoute,
                        label: getDivisionLabel(
                          comprehensiveMerchantListItems,
                          searchInput.contractRoute
                        ),
                      }}
                      options={comprehensiveMerchantListItems}
                      filteringType="auto"
                      onChange={({ detail: { selectedOption } }) =>
                        setSearchInput({
                          ...searchInput,
                          contractRoute: selectedOption.value!,
                        })
                      }
                    />
                  </FormField>
                </Grid>
              )}
            </ColumnLayout>
            <ColumnLayout columns={4}>
            <FormField
                label={LABELS.SITE_ID}
                errorText={cpidErrorMsg}
                constraintText={PLACE_APPLICATION.FONT_SIZE_CPID}
              >
                <Input
                  name="siteId"
                  value={searchInput.cpid}
                  onChange={({ detail: { value } }) => {
                    setSearchInput({ ...searchInput, cpid: value });
                    checkApplicationCpid(value);
                  }}
                />
                </FormField></ColumnLayout>
            <ColumnLayout columns={6}>
              <PeriodDatePicker
                label={LABELS.APPLICATION_START_DATE}
                startDate={searchInput.startDate}
                startDateOnChange={({ detail: { value } }) =>
                  setSearchInput({
                    ...searchInput,
                    startDate: formatDate(value),
                  })
                }
                endDate={searchInput.endDate}
                endDateOnChange={({ detail: { value } }) =>
                  setSearchInput({
                    ...searchInput,
                    endDate: formatDate(value),
                  })
                }
              />
              <SpaceBetween direction="horizontal" size="l">
                <FormField label={LABELS.ANSWER_START_TIME}>
                  <DatePicker
                    onChange={({ detail: { value } }) =>
                      setSearchInput({
                        ...searchInput,
                        fixedTimeStart: fixedFormatDateTime(
                          value,
                          isEmpty(value) ? "" : fixedTimeStart
                        ),
                      })
                    }
                    value={fixedDateStart}
                  />
                </FormField>
                <FormField label={LABELS.EMPTY}>
                  <div style={{ width: "80px" }}>
                    <TimeInput
                      onChange={({ detail: { value } }) =>
                        setSearchInput({
                          ...searchInput,
                          fixedTimeStart: fixedFormatDateTime(
                            fixedDateStart,
                            value
                          ),
                        })
                      }
                      value={fixedTimeStart}
                      format="hh:mm"
                      placeholder="HH:MM"
                      disabled={isEmpty(fixedDateStart)}
                    />
                  </div>
                </FormField>

                <Box padding={{ top: "xxl" }} variant="awsui-key-label">
                  {LABELS.APPLICATION_START}
                </Box>
                <FormField label={LABELS.EMPTY}>
                  <DatePicker
                    onChange={({ detail: { value } }) =>
                      setSearchInput({
                        ...searchInput,
                        fixedTimeEnd: fixedFormatDateTime(
                          value,
                          isEmpty(value) ? "" : fixedTimeEnd
                        ),
                      })
                    }
                    value={fixedDateEnd}
                  />
                </FormField>
                <FormField label={LABELS.EMPTY}>
                  <div style={{ width: "80px" }}>
                    <TimeInput
                      onChange={({ detail: { value } }) =>
                        setSearchInput({
                          ...searchInput,
                          fixedTimeEnd: fixedFormatDateTime(
                            fixedDateEnd,
                            value
                          ),
                        })
                      }
                      value={fixedTimeEnd}
                      format="hh:mm"
                      placeholder="HH:MM"
                      disabled={isEmpty(fixedDateEnd)}
                    />
                  </div>
                </FormField>
              </SpaceBetween>
            </ColumnLayout>
            <ColumnLayout columns={4}>
              <FormField
                label={LABELS.COMPANY_NAME}
                errorText={companyNameErrorMsg}
                constraintText={PLACE_APPLICATION.FONT_SIZE}
              >
                <Input
                  name="companyName"
                  value={searchInput.companyName}
                  onChange={({ detail: { value } }) => {
                    setSearchInput({
                      ...searchInput,
                      companyName: value,
                    });
                    checkCompanyName(value);
                  }}
                />
              </FormField>
              <FormField
                label={LABELS.MERCHANTS_NAME}
                errorText={merchantsNameErrorMsg}
                constraintText={PLACE_APPLICATION.FONT_SIZE_MERCHANTS}
              >
                <Input
                  name="merchantName"
                  value={searchInput.merchantName}
                  onChange={({ detail: { value } }) => {
                    setSearchInput({
                      ...searchInput,
                      merchantName: value,
                    });
                    checkMerchantsName(value);
                  }}
                />
              </FormField>
              {isDocomoUser && (
                <SpaceBetween direction="horizontal" size="l">
                  <FormField label={LABELS.PARTICULAR_RATE_FLAG}>
                    <Select
                      selectedOption={{
                        value: searchInput.particularRateFlag,
                        label: getDivisionLabel(
                          PARTICULAR_RATE_FLAG_LIST,
                          searchInput.particularRateFlag
                        ),
                      }}
                      options={PARTICULAR_RATE_FLAG_LIST}
                      onChange={({ detail: { selectedOption } }) =>
                        setSearchInput({
                          ...searchInput,
                          particularRateFlag: selectedOption.value!,
                        })
                      }
                    />
                  </FormField>
                </SpaceBetween>
              )}
            </ColumnLayout>
            <FormField label={LABELS.APPLICATION_TYPE}>
              <SpaceBetween direction="horizontal" size="xl">
                {applicationType.map((updateDifferencial) => (
                  <Checkbox
                    key={updateDifferencial.label}
                    onChange={() =>
                      updateDifferencialOnChange(updateDifferencial.value)
                    }
                    checked={searchInput.applicationTypes.includes(
                      updateDifferencial.value
                    )}
                  >
                    {updateDifferencial.label}
                  </Checkbox>
                ))}
              </SpaceBetween>
            </FormField>
            <FormField label={LABELS.APPLICATION_STATUS_TITLE}>
              <SpaceBetween direction="horizontal" size="xl">
                {applicationStatus.map((item) => (
                  <Checkbox
                    key={item.label}
                    onChange={() => applicationStatusOnChange(item.value)}
                    checked={searchInput.applicationStatuses.includes(
                      item.value
                    )}
                  >
                    {item.label}
                  </Checkbox>
                ))}
              </SpaceBetween>
            </FormField>
            {isDocomoUser && (
              <FormField label={LABELS.APPLICATION_STATUS_DETAILS}>
                <SpaceBetween direction="horizontal" size="xl">
                  {appDetailStatusList.map((item) => (
                    <Checkbox
                      key={item.label}
                      onChange={() =>
                        applicationDetailStatusOnChange(item.value)
                      }
                      checked={searchInput.applicationDetailStatuses.includes(
                        item.value
                      )}
                    >
                      {item.label}
                    </Checkbox>
                  ))}
                </SpaceBetween>
              </FormField>
            )}
            {isDocomoUser && (
              <FormField label={LABELS.APPLICANT}>
                <SpaceBetween direction="horizontal" size="xl">
                  {applicantUserTypeList.map((item) => (
                    <Checkbox
                      key={item.label}
                      onChange={() => applicantUserTypeOnChange(item.value)}
                      checked={searchInput.applicantUserType.includes(
                        item.value
                      )}
                    >
                      {item.label}
                    </Checkbox>
                  ))}
                </SpaceBetween>
              </FormField>
            )}
            {isDocomoUser && (
              <FormField
                label={LABELS.APPLICANT_EMAIL}
                errorText={applicantEmailErrorMsg}
                constraintText={PLACE_APPLICATION.FONT_SIZE_EMAIL}
              >
                <Input
                  name="applicantEmail"
                  value={searchInput.applicantEmail}
                  onChange={({ detail: { value } }) => {
                    setSearchInput({
                      ...searchInput,
                      applicantEmail: value,
                    });
                    checkApplicationEmail(value);
                  }}
                />
              </FormField>
            )}
            {isApply && applicationFormTypeList && (
              <FormField label={LABELS.APPLICATION_FORM_TYPE}>
                <SpaceBetween direction="horizontal" size="xl">
                  {applicationFormTypeList.slice().sort((a, b) => a.label.localeCompare(b.label, "ja")).map((item) => (
                    <Checkbox
                      key={item.label}
                      onChange={() => applicationFormTypeOnChange(item.label)}
                      checked={
                        searchInput.applicationFormType.some(it => it === item.label)
                      }
                    >
                      {item.label}
                    </Checkbox>
                  ))}
                </SpaceBetween>
            </FormField>
            )}
          </SpaceBetween>
        </SpaceBetween>
      </Container>
    </Form>
  );
};

export default SearchBar;
